/* WeQuote Dev Board — state provider. Board data comes from /api/devboard
 * (server persistence); pure mutations from core/mutations run optimistically
 * and each changed card is persisted with a whole-card PUT. The pop-up state
 * (modalCardId, read/edit, tab) lives here so EVERY view opens the same
 * consistent pop-up (PRD §6). The comment author is the REAL logged-in agent. */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { Feature, Blockout, DevInfo } from './core/types'
import { setDevRoster, inPipeline } from './core/constants'
import { setScheduleBlockouts } from './core/logic'
import type { MutResult } from './core/mutations'
import { fetchBoard, saveCard, createCard, deleteCard, createBlockout, deleteBlockout } from './core/api'
import * as R from './core/review'
import { computeSchedule } from './core/schedule'
import type { FocusFilter } from './core/suggest'
import type { OvMonth } from './core/overview'
import { useAuth } from '../../../contexts/AuthContext'

export type View =
  | 'overview' | 'roadmap' | 'calendar' | 'review' | 'detail' | 'list'
  | 'board' | 'completed' | 'archived' | 'ideas' | 'myboard' | 'relnotes'

export type SubModal =
  | null
  | { kind: 'addRequest'; id: number }
  | { kind: 'chaseLog'; id: number; company: string; person: string }
  | { kind: 'chaseHistory'; id: number; company: string; person: string }
  | { kind: 'archive'; id: number }
  | { kind: 'markLive'; id: number }
  | { kind: 'pinConfirm'; id: number; pos: number }
  | { kind: 'suggestInfo'; id: number }
  | { kind: 'suggestHelp' }
  | { kind: 'resetSuggest' }
  | { kind: 'merge'; id: number }
  | { kind: 'addFeature' }
  | { kind: 'schedule'; id: number } // v2 §3: the Schedule action

interface Ctx {
  loading: boolean
  error: string | null
  cards: Feature[]
  blockouts: Blockout[]
  roster: DevInfo[]
  currentUserName: string
  view: View
  setView: (v: View) => void
  navCollapsed: boolean
  toggleNav: () => void
  badges: { review: number; priority: number; archived: number; ideas: number }
  /* the shared pop-up */
  modalCardId: number | null
  detailEditMode: boolean
  detailTab: 'notes' | 'requests'
  openCardModal: (id: number) => void
  closeCardModal: () => void
  toggleDetailEdit: () => void
  setDetailTab: (t: 'notes' | 'requests') => void
  subModal: SubModal
  openSubModal: (m: SubModal) => void
  /* mutations */
  mutate: (fn: (fs: Feature[]) => MutResult) => void
  addFeatureCard: (payload: Partial<Feature>) => Promise<void>
  /* review / intake */
  pendingFocusCard: number | null
  consumeFocusCard: () => void
  addFeature: (f: R.AddFeatureFields) => Promise<void>
  importChatRequest: () => Promise<void>
  resolveAddToDev: (id: number) => void
  resolveMerge: (sourceId: number, targetId: number) => void
  /* roadmap */
  sched: { start: Record<number, string>; conflict: Record<number, boolean> }
  /* overview */
  ovYear: number
  ovMonth: OvMonth
  setOvYear: (y: number) => void
  setOvMonth: (m: OvMonth) => void
  focusFilter: FocusFilter
  setFocusFilterManual: (f: FocusFilter) => void
  openFocusFilter: (mode: FocusFilter) => void
  addBlockoutAction: (b: { name: string; start: string; end: string; scope: string }) => Promise<void>
  removeBlockoutAction: (id: number) => Promise<void>
  removeCard: (id: number) => Promise<void>
  toast: string | null
  showToast: (msg: string) => void
  reqDatalists: { companies: string[]; people: string[] }
}

const C = createContext<Ctx | null>(null)
export const useDevBoard = (): Ctx => {
  const ctx = useContext(C)
  if (!ctx) throw new Error('useDevBoard must be used within DevBoardProvider')
  return ctx
}

export const DevBoardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentAgent } = useAuth()
  const currentUserName = currentAgent?.name || 'Unknown'
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cards, setCards] = useState<Feature[]>([])
  const [blockouts, setBlockouts] = useState<Blockout[]>([])
  const [roster, setRoster] = useState<DevInfo[]>([])
  // The active view survives reloads (reference saveState behaviour), per user.
  const VIEWS: View[] = ['overview', 'myboard', 'roadmap', 'calendar', 'review', 'detail', 'list', 'board', 'completed', 'relnotes', 'archived', 'ideas']
  const viewKey = 'wq-devboard-view:' + (currentAgent?.id || 'anon')
  const [view, setViewState] = useState<View>(() => {
    try {
      const v = localStorage.getItem(viewKey) as View | null
      // v2 §4: dev-only logins land on THEIR board by default (first visit);
      // their saved view still wins on later visits.
      const fallback: View = (currentAgent as any)?.devOnly ? 'myboard' : 'detail'
      return v && VIEWS.includes(v) ? v : fallback
    } catch { return (currentAgent as any)?.devOnly ? 'myboard' : 'detail' }
  })
  useEffect(() => {
    try { localStorage.setItem(viewKey, view) } catch { /* per-user convenience only */ }
  }, [view, viewKey])
  const [navCollapsed, setNavCollapsed] = useState(false)
  const [modalCardId, setModalCardId] = useState<number | null>(null)
  const [detailEditMode, setDetailEditMode] = useState(false)
  const [detailTab, setDetailTabState] = useState<'notes' | 'requests'>('notes')
  const [subModal, setSubModal] = useState<SubModal>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [pendingFocusCard, setPendingFocusCard] = useState<number | null>(null)
  const [ovYear, setOvYear] = useState(() => new Date().getFullYear())
  const [ovMonth, setOvMonth] = useState<OvMonth>(() => new Date().getMonth())
  const [focusFilter, setFocusFilter] = useState<FocusFilter>('all')
  const focusFromTileRef = useRef(false)
  const chatIdx = useRef(0)
  const toastTimer = useRef<number>(0)
  const cardsRef = useRef<Feature[]>([])
  const blockoutsRef = useRef<Blockout[]>([])
  const mutateRef = useRef<(fn: (fs: Feature[]) => MutResult) => void>(() => {})

  useEffect(() => { cardsRef.current = cards }, [cards])
  useEffect(() => { blockoutsRef.current = blockouts }, [blockouts])

  useEffect(() => {
    let alive = true
    fetchBoard()
      .then((d) => {
        if (!alive) return
        if (d.roster.length) setDevRoster(d.roster)
        setScheduleBlockouts(d.blockouts)
        setCards(d.cards)
        setBlockouts(d.blockouts)
        setRoster(d.roster)
        setLoading(false)
      })
      .catch((e) => { if (alive) { setError(e.message || 'Failed to load the board.'); setLoading(false) } })
    return () => { alive = false }
  }, [])

  const showToast = useCallback((msg: string) => {
    setToast(msg)
    window.clearTimeout(toastTimer.current)
    toastTimer.current = window.setTimeout(() => setToast(null), 3400)
  }, [])

  /** Optimistic apply + persist each changed card (whole-card PUT, last-write-wins). */
  const mutate = useCallback((fn: (fs: Feature[]) => MutResult) => {
    setCards((prev) => {
      const dec = computeSchedule(prev, blockoutsRef.current).features
      const r = fn(dec)
      if (r.toast) showToast(r.toast)
      if (r.features === dec) return prev
      const ids = r.changedIds || []
      ids.forEach((id) => {
        const card = r.features.find((f) => f.id === id)
        if (card) saveCard(card).catch(() => showToast('Save failed — check your connection and retry.'))
      })
      return r.features
    })
  }, [showToast])
  useEffect(() => { mutateRef.current = mutate }, [mutate])

  const addFeatureCard = useCallback(async (payload: Partial<Feature>) => {
    const card = await createCard(payload)
    setCards((prev) => [...prev, card])
  }, [])
  const removeCard = useCallback(async (id: number) => {
    await deleteCard(id)
    setCards((prev) => prev.filter((f) => f.id !== id))
    setModalCardId((cur) => (cur === id ? null : cur))
  }, [])

  const setView = useCallback((v: View) => {
    setViewState((prev) => {
      const leavingPriority = (prev === 'detail' || prev === 'list') && v !== 'detail' && v !== 'list'
      if (leavingPriority && focusFromTileRef.current) { setFocusFilter('all'); focusFromTileRef.current = false }
      return v
    })
  }, [])
  const consumeFocusCard = useCallback(() => setPendingFocusCard(null), [])

  /** Overview tile → Priority List with a ONE-VISIT category filter (§45):
   *  leaving the Priority List clears it; a manually chosen filter is sticky. */
  const openFocusFilter = useCallback((mode: FocusFilter) => {
    setFocusFilter(mode)
    focusFromTileRef.current = true
    setModalCardId(null)
    setDetailEditMode(false)
    setViewState('detail')
  }, [])
  const setFocusFilterManual = useCallback((f: FocusFilter) => {
    setFocusFilter(f)
    focusFromTileRef.current = false
  }, [])

  /** After resolving from the Overview pop-up, stay put; otherwise land on the card in Focus. */
  const navigateAfterResolve = useCallback((cardId: number | null) => {
    setModalCardId(null)
    setDetailEditMode(false)
    setSubModal(null)
    setViewState((v) => {
      if (v === 'overview') return v
      if (cardId != null) setPendingFocusCard(cardId)
      return 'detail'
    })
  }, [])

  const addFeature = useCallback(async (f: R.AddFeatureFields) => {
    if (!f.title.trim()) { showToast('Please enter a feature name.'); return }
    if (!f.module) { showToast('Pick which WeQuote module this is for — it\u2019s required.'); return }
    if (f.start && f.deadline && f.start > f.deadline) { showToast('Start date can’t be after the deadline.'); return }
    const po = cardsRef.current.reduce((m, x) => Math.max(m, x.po), 0) + 1
    const payload = R.buildManualCard(po, f, currentUserName)
    const card = await createCard(payload)
    setCards((prev) => [...prev, card])
    setSubModal(null)
    setViewState('review')
    showToast('New request added to Needs Review — alert emailed to ' + (card.alertEmailedTo || 'the sign-off group'))
  }, [currentUserName, showToast])

  const importChatRequest = useCallback(async () => {
    const po = cardsRef.current.reduce((m, x) => Math.max(m, x.po), 0) + 1
    const payload = R.buildChatCard(po, chatIdx.current)
    chatIdx.current += 1
    const card = await createCard(payload)
    setCards((prev) => [...prev, card])
    setViewState('review')
    showToast('New request pulled from support chat · review alert emailed to ' + (card.alertEmailedTo || 'the sign-off group'))
  }, [showToast])

  const resolveAddToDev = useCallback((id: number) => {
    mutateRef.current((fs) => R.reviewAddToDev(fs, id))
    navigateAfterResolve(id)
  }, [navigateAfterResolve])

  const resolveMerge = useCallback((sourceId: number, targetId: number) => {
    setCards((prev) => {
      const r = R.mergeInto(prev, sourceId, targetId)
      if (r.toast) showToast(r.toast)
      if (r.features === prev) return prev
      const tgt = r.features.find((f) => f.id === targetId)
      if (tgt) saveCard(tgt).catch(() => showToast('Save failed — check your connection and retry.'))
      if (r.removedId) deleteCard(r.removedId).catch(() => showToast('Could not remove the merged card on the server.'))
      return r.features
    })
    navigateAfterResolve(targetId)
  }, [navigateAfterResolve, showToast])
  const toggleNav = useCallback(() => setNavCollapsed((x) => !x), [])
  const openCardModal = useCallback((id: number) => { setModalCardId(id); setDetailEditMode(false); setDetailTabState('notes') }, [])
  const closeCardModal = useCallback(() => { setModalCardId(null); setDetailEditMode(false); setSubModal(null) }, [])
  const toggleDetailEdit = useCallback(() => setDetailEditMode((v) => !v), [])
  const setDetailTab = useCallback((t: 'notes' | 'requests') => setDetailTabState(t), [])
  const openSubModal = useCallback((m: SubModal) => setSubModal(m), [])

  const scheduled = useMemo(() => computeSchedule(cards, blockouts), [cards, blockouts])

  const addBlockoutAction = useCallback(async (b: { name: string; start: string; end: string; scope: string }) => {
    if (!b.name.trim() || !b.start || !b.end) { showToast('Give the block-out a name, a start and an end date.'); return }
    const created = await createBlockout(b)
    setBlockouts((prev) => [...prev, created])
  }, [showToast])
  const removeBlockoutAction = useCallback(async (id: number) => {
    await deleteBlockout(id)
    setBlockouts((prev) => prev.filter((x) => x.id !== id))
  }, [])

  const badges = useMemo(() => ({
    review: cards.filter((f) => f.status === 'needsreview').length,
    priority: cards.filter(inPipeline).length,
    archived: cards.filter((f) => f.status === 'archived').length,
    ideas: cards.filter((f) => f.status === 'idea').length,
  }), [cards])

  const reqDatalists = useMemo(() => {
    const companies: Record<string, 1> = {}
    const people: Record<string, 1> = {}
    cards.forEach((f) => (f.requests || []).forEach((r) => {
      if (r.company && ('' + r.company).trim()) companies[('' + r.company).trim()] = 1
      if (r.person && ('' + r.person).trim()) people[('' + r.person).trim()] = 1
    }))
    return { companies: Object.keys(companies).sort(), people: Object.keys(people).sort() }
  }, [cards])

  const value = useMemo<Ctx>(() => ({
    loading, error, cards: scheduled.features, blockouts, roster, currentUserName,
    view, setView, navCollapsed, toggleNav, badges,
    modalCardId, detailEditMode, detailTab, openCardModal, closeCardModal, toggleDetailEdit, setDetailTab,
    subModal, openSubModal,
    mutate, addFeatureCard, removeCard, toast, showToast, reqDatalists,
    pendingFocusCard, consumeFocusCard, addFeature, importChatRequest, resolveAddToDev, resolveMerge,
    sched: { start: scheduled.start, conflict: scheduled.conflict }, addBlockoutAction, removeBlockoutAction,
    ovYear, ovMonth, setOvYear, setOvMonth, focusFilter, setFocusFilterManual, openFocusFilter,
  }), [loading, error, cards, blockouts, roster, currentUserName, view, setView, navCollapsed, toggleNav, badges, modalCardId, detailEditMode, detailTab, openCardModal, closeCardModal, toggleDetailEdit, setDetailTab, subModal, openSubModal, mutate, addFeatureCard, removeCard, toast, showToast, reqDatalists, pendingFocusCard, consumeFocusCard, addFeature, importChatRequest, resolveAddToDev, resolveMerge, scheduled, addBlockoutAction, removeBlockoutAction, ovYear, ovMonth, focusFilter, setFocusFilterManual, openFocusFilter])

  return <C.Provider value={value}>{children}</C.Provider>
}
